import { createBrowserRouter, Navigate } from "react-router-dom";

import { AdminPortalLayout } from "@/app/layouts/AdminPortalLayout";
import { RootLayout } from "@/app/layouts/RootLayout";
import { StudentPortalLayout } from "@/app/layouts/StudentPortalLayout";
import { TeacherPortalLayout } from "@/app/layouts/TeacherPortalLayout";
import { HomePage } from "@/app/pages/HomePage";
import { NotFoundPage } from "@/app/pages/NotFoundPage";
import { PortalChooserPage } from "@/app/pages/PortalChooserPage";
import { RouteErrorPage } from "@/app/pages/RouteErrorPage";
import { StudentsPage } from "@/app/pages/StudentsPage";
import { ProtectedRoute, Permissions, Roles } from "@/features/auth";
import { ADMIN_NAVIGATION } from "../navigation/admin-navigation";
import { STUDENT_NAVIGATION } from "../navigation/student-navigation";
import { TEACHER_NAVIGATION } from "../navigation/teacher-navigation";
import { RouteLoading } from "../pages/RouteLoading";
import { adminMessageRoute } from "./admin-message-routes";
import { adminPollDetailRoutes } from "./admin-poll-routes";
import { adminConductDetailRoutes } from "./admin-conduct-routes";
import { adminPeopleDetailRoutes } from "./admin-people-routes";

/** Central route tree composing layouts, public pages, and protected routes. */
export const router = createBrowserRouter([
  ...(import.meta.env.DEV
    ? [
        {
          path: "/preview/student-print",
          lazy: () => import("@/app/pages/StudentPrintDemoPage"),
        },
      ]
    : []),
  {
    path: "/",
    Component: RootLayout,
    ErrorBoundary: RouteErrorPage,
    HydrateFallback: RouteLoading,
    children: [
      { index: true, Component: HomePage },
      { path: "about", lazy: () => import("@/app/pages/AboutPage") },
      {
        path: "profile",
        lazy: async () =>
          (await import("@/features/auth/route")).loadProfileRoute(),
      },
      { path: "login", lazy: () => import("@/features/auth/route") },
      {
        path: "portals",
        element: (
          <ProtectedRoute>
            <PortalChooserPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "students",
        element: (
          <ProtectedRoute requiredPermission={Permissions.StudentsRead}>
            <StudentsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "teacher",
        element: (
          <ProtectedRoute requiredRole={Roles.Teacher}>
            <TeacherPortalLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to={TEACHER_NAVIGATION[0].path} replace />,
          },
          ...TEACHER_NAVIGATION.map(({ segment, lazy }) => ({
            path: segment,
            lazy,
          })),
          {
            path: "sessions",
            element: <Navigate to={TEACHER_NAVIGATION[0].path} replace />,
          },
        ],
      },
      {
        path: "student",
        element: (
          <ProtectedRoute requiredRole={Roles.Student}>
            <StudentPortalLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to={STUDENT_NAVIGATION[0].path} replace />,
          },
          ...STUDENT_NAVIGATION.map(({ segment, lazy }) => ({
            path: segment,
            lazy,
          })),
        ],
      },
      {
        // Marking owns the same standalone wizard chrome as poll creation.
        path: "teacher/scores/create",
        lazy: async () =>
          (await import("@/features/scoring/route")).loadScoreWizardRoute(),
      },
      {
        path: "teacher/scores/gradebook",
        lazy: async () =>
          (await import("@/features/scoring/route")).loadGradebookRoute(),
      },
      {
        // Each destination tab authenticates and redirects itself; the portal stays in place.
        path: "teacher/join",
        lazy: async () =>
          (
            await import("@/features/classroom-sessions/route")
          ).loadMeetingJoinRoute(),
      },
      {
        path: "student/join",
        lazy: async () =>
          (
            await import("@/features/student-portal/route")
          ).loadMeetingJoinRoute(),
      },
      {
        // A normal new-tab link loads a page that owns its join request and redirects only itself.
        path: "admin/join",
        lazy: () => import("@/features/admin-portal/route"),
      },
      {
        // Help articles are standalone reading pages with their own back navigation.
        path: "admin/help/:slug",
        lazy: async () =>
          (await import("@/features/admin-help/route")).loadHelpArticleRoute(),
      },
      {
        path: "admin/monitoring/review-students",
        lazy: async () =>
          (
            await import("@/features/admin-monitoring/route")
          ).loadReviewStudentsRoute(),
      },
      {
        path: "admin/monitoring/score-coverage/print",
        lazy: async () =>
          (
            await import("@/features/admin-monitoring/route")
          ).loadScoreCoveragePrintRoute(),
      },
      {
        path: "admin/monitoring/score-coverage",
        lazy: async () =>
          (
            await import("@/features/admin-monitoring/route")
          ).loadScoreCoverageRoute(),
      },
      adminMessageRoute,
      ...adminConductDetailRoutes,
      // People drill-down pages own their headers and omit the main portal navigation.
      ...adminPeopleDetailRoutes,
      ...adminPollDetailRoutes,
      {
        path: "admin",
        element: (
          <ProtectedRoute requiredRole={Roles.Admin}>
            <AdminPortalLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to={ADMIN_NAVIGATION[0].path} replace />,
          },
          ...ADMIN_NAVIGATION.filter(
            ({ segment }) => segment !== "messages",
          ).map(({ segment, lazy }) => ({
            path: segment,
            lazy,
          })),
          {
            // Per-classroom detail, opened by tapping the dashboard's chart. It lives inside the
            // portal layout but outside the bottom-nav tabs: it is a drill-down, not a
            // destination, so it must not add a nav entry that would highlight nothing.
            path: "absences/classrooms",
            lazy: async () => ({
              Component: (await import("@/features/admin-portal"))
                .AdminAbsenceClassroomsPage,
            }),
          },
        ],
      },
      { path: "a/:code", lazy: () => import("@/features/sms-approval/route") },
      {
        path: ":code",
        lazy: async () =>
          (await import("@/features/polls/route")).loadPublicPollRoute(),
      },
      { path: "*", Component: NotFoundPage },
    ],
  },
]);
